import { Channel, ChannelSchedule, TimeRange } from '../services/api/models'

function isTimeInRanges(hours: number, minutes: number, timeRanges: TimeRange[]): boolean {
  const currentMinutes = hours * 60 + minutes

  return timeRanges.some((range) => {
    const [startHours, startMinutes] = range.startTime.split(':').map(Number)
    const [endHours, endMinutes] = range.endTime.split(':').map(Number)

    const startTotalMinutes = startHours * 60 + startMinutes
    const endTotalMinutes = endHours * 60 + endMinutes

    return currentMinutes >= startTotalMinutes && currentMinutes <= endTotalMinutes
  })
}

/**
 * Verifica si un canal está activo según su configuración de horario.
 * Port del método isChannelActive del modelo Device en flyiot-models.
 *
 * La convención de días es la nativa de JavaScript: 0 = Domingo, 1 = Lunes, ..., 6 = Sábado.
 *
 * @param channel - El canal con su configuración de schedule
 * @param customDate - Fecha opcional para testing (por defecto new Date())
 * @returns true si el canal está activo (se permite enviar comandos)
 */
export function isChannelActive(channel: Channel, customDate?: Date): boolean {
  if (!channel.schedule || !channel.schedule.enabled) {
    return true
  }

  const utcNow = customDate || new Date()
  const schedule: ChannelSchedule = channel.schedule

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: schedule.timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  })

  const parts = formatter.formatToParts(utcNow)

  const dateComponents: Record<string, number> = {}
  for (const part of parts) {
    if (part.type !== 'literal') {
      dateComponents[part.type] = parseInt(part.value, 10)
    }
  }

  const hours = dateComponents.hour
  const minutes = dateComponents.minute

  const localDate = new Date(
    dateComponents.year,
    dateComponents.month - 1,
    dateComponents.day,
  )

  const dayOfWeek = localDate.getDay()

  if (!schedule.days.includes(dayOfWeek)) {
    return false
  }

  const todayString = `${dateComponents.year}-${String(dateComponents.month).padStart(2, '0')}-${String(dateComponents.day).padStart(2, '0')}`

  const exception = schedule.exceptions?.find((exc) => {
    const excDateString = exc.date.split('T')[0]
    return excDateString === todayString
  })

  if (exception) {
    if (!exception.enabled) {
      return false
    }

    if (exception.timeRanges && exception.timeRanges.length > 0) {
      return isTimeInRanges(hours, minutes, exception.timeRanges)
    }
  }

  return isTimeInRanges(hours, minutes, schedule.timeRanges)
}
